import { useEffect, useState, type ReactNode } from 'react';
import { Card, Title, Text, PasswordInput, Button, Center, Stack, Loader } from '@mantine/core';
import { ping, setAdminKey, getAdminKey, clearAdminKey, shouldShowEndpoint, resolveApiBase } from '../lib/api';

export function AuthGate({ children }: { children: ReactNode }) {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [key, setKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      if (getAdminKey()) {
        const ok = await ping();
        if (mounted) setAuthed(ok);
        if (!ok) clearAdminKey();
      }
      if (mounted) setChecking(false);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const submit = async () => {
    if (!key.trim()) return;
    setSubmitting(true);
    setError(null);
    setAdminKey(key.trim());
    const ok = await ping();
    setSubmitting(false);
    if (ok) setAuthed(true);
    else {
      clearAdminKey();
      setError('Invalid admin key.');
    }
  };

  if (checking) {
    return (
      <Center h="100vh">
        <Loader color="brand" />
      </Center>
    );
  }

  if (authed) return <>{children}</>;

  return (
    <Center h="100vh" bg="#f5f6f8">
      <Card w={360} padding="xl" radius="lg" withBorder>
        <Title order={3} c="navy.9">
          ulaim
        </Title>
        <Text size="sm" c="dimmed" mt={2}>
          Executive analytics
        </Text>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Stack gap="sm" mt="lg">
            <PasswordInput
              placeholder="Admin key"
              value={key}
              onChange={(e) => setKey(e.currentTarget.value)}
              error={error}
              data-autofocus
              size="md"
            />
            <Button type="submit" loading={submitting} disabled={!key.trim()} fullWidth size="md">
              Enter
            </Button>
          </Stack>
        </form>
        {shouldShowEndpoint() && (
          <Text size="xs" c="dimmed" mt="md" ff="monospace">
            {resolveApiBase()}
          </Text>
        )}
      </Card>
    </Center>
  );
}
